/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const MENU_PERMISSION_BY_PATH = {
  '/':'menu:dashboard', '/projects':'menu:projects', '/tasks':'menu:tasks', '/workload':'menu:workload', '/absences':'menu:absences',
  '/requirements':'menu:requirements', '/time':'menu:time', '/tests':'menu:tests', '/sprints':'menu:sprints', '/governance':'menu:governance',
  '/bi':'menu:bi', '/settings':'menu:settings', '/invoicing':'menu:invoicing', '/expenses':'menu:expenses', '/validations':'menu:validations',
  '/admin':'menu:admin', '/about':'menu:about', '/technical':'menu:technical', '/notifications':'menu:dashboard', '/interventions':'menu:interventions', '/assets':'menu:assets','/suppliers':'menu:assets',
};

export default function ProtectedRoute({ children, module }) {
  const { user, loading, enabledMenus, hasPermission } = useAuth();
  const location = useLocation();

  // Determine the module path to check (defaults to current pathname)
  const modulePath = module || location.pathname;
  // REGLE DE GESTION : le backend laisse l'administrateur acceder aux modules pour
  // pouvoir les reparer/reactiver. Le frontend applique exactement la meme regle.
  const effectiveRoles = user?.role_codes || [user?.role, ...(user?.additional_roles || [])].filter(Boolean);
  const moduleDisabled = !effectiveRoles.includes('admin') && !(user?.permissions || []).includes('*') && enabledMenus && enabledMenus[modulePath] === false;
  // REGLE DE GESTION : une URL saisie manuellement doit respecter le meme droit menu:* que la sidebar.
  const menuPermission = MENU_PERMISSION_BY_PATH[modulePath] || MENU_PERMISSION_BY_PATH['/' + String(modulePath).split('/').filter(Boolean)[0]];
  const permissionsReady = !!user && Array.isArray(user?.permissions);
  const menuDenied = !!menuPermission && permissionsReady && !hasPermission(menuPermission);

  useEffect(() => {
    if (moduleDisabled) toast.error("Ce module a été désactivé par l'administrateur.");
    else if (menuDenied) toast.error("Vous n'avez pas les droits nécessaires pour accéder à ce module.");
  }, [moduleDisabled, menuDenied]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (moduleDisabled || menuDenied) {
    return <Navigate to="/" replace />;
  }

  if (user?.role === 'client' || user?.user_type === 'internal_client') {
    const allowed = ['/', '/notifications', '/about'];
    const ok = allowed.some(p => p === '/' ? location.pathname === '/' : location.pathname === p || location.pathname.startsWith(`${p}/`));
    if (!ok) return <Navigate to="/" replace />;
  }

  return children;
}
